/**
 * Skinique Customer Stories
 * Visibility settings drive card sizing; few stories stay centered
 */
(function () {
  if (window.__skCustomerStoriesLoaded) return;
  window.__skCustomerStoriesLoaded = true;

  const SELECTOR = '[data-sk-stories]';

  function parseConfig(root) {
    try {
      return JSON.parse(root.getAttribute('data-sk-stories-config') || '{}');
    } catch (e) {
      return {};
    }
  }

  function getVisible(config) {
    const w = window.innerWidth;
    if (w < 750) return Math.max(1, Number(config.mobile) || 1);
    if (w < 990) return Math.max(1, Number(config.tablet) || 2);
    if (w < 1400) return Math.max(1, Number(config.laptop) || 3);
    return Math.max(1, Number(config.desktop) || 3);
  }

  function applyLayoutVars(root, config, count) {
    const viewport = root.querySelector('.sk-stories__viewport');
    const visible = getVisible(config);
    const fitAll = count <= visible;
    const width = viewport ? viewport.clientWidth : root.clientWidth;
    const gap = root.classList.contains('sk-stories--drawer') ? 6 : 8;
    const slideW = Math.max(110, Math.floor(width / visible));
    const cardW = Math.max(100, slideW - gap);

    root.style.setProperty('--sk-stories-visible', String(visible));
    root.style.setProperty('--sk-stories-slide-w', `${slideW}px`);
    root.style.setProperty('--sk-stories-card-w', `${cardW}px`);
    root.classList.toggle('sk-stories--fit', fitAll);
    root.dataset.skStoriesVisible = String(visible);
    root.dataset.skStoriesFit = fitAll ? 'true' : 'false';

    return { visible, fitAll, slideW, cardW };
  }

  function getVideo(card) {
    if (!card) return null;
    return card.querySelector('video');
  }

  function pauseAll(root, except) {
    root.querySelectorAll('[data-sk-stories-card]').forEach((card) => {
      const video = getVideo(card);
      if (!video) return;
      if (card !== except) {
        try {
          video.pause();
        } catch (e) {}
        card.classList.remove('is-playing');
      }
    });
  }

  function prepareVideo(video) {
    if (!video) return;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.playsInline = true;
    video.controls = false;
    // Prefetch so first click can play inside the user-gesture window
    if (video.preload !== 'auto') video.preload = 'auto';
  }

  function startPlayback(video, card) {
    prepareVideo(video);
    video.muted = true;

    const markPlaying = () => card.classList.add('is-playing');
    const markStopped = () => card.classList.remove('is-playing');

    // Do NOT call video.load() here — it resets media and cancels play()
    // so the first click appears to "do nothing".
    const attempt = () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        return playPromise.then(() => {
          markPlaying();
          try {
            video.muted = false;
            video.volume = 1;
          } catch (e) {}
        });
      }
      markPlaying();
      return Promise.resolve();
    };

    return attempt().catch(() => {
      video.muted = true;
      return video.play().then(markPlaying);
    }).catch(markStopped);
  }

  function togglePlay(card, root) {
    const video = getVideo(card);
    if (!video) return false;

    if (!video.paused && !video.ended) {
      video.pause();
      card.classList.remove('is-playing');
      return true;
    }

    pauseAll(root, card);
    startPlayback(video, card);
    return true;
  }

  function prefetchCard(card) {
    const video = getVideo(card);
    if (!video) return;
    prepareVideo(video);
    // Warm the buffer without playing
    try {
      if (video.readyState < 2) {
        video.load();
      }
    } catch (e) {}
  }

  function bindRootClicks(root, $slick) {
    if (root.dataset.skStoriesClicksBound === 'true') return;
    root.dataset.skStoriesClicksBound = 'true';

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-sk-stories-cta]')) return;

      const card = event.target.closest('[data-sk-stories-card]');
      if (!card || !root.contains(card)) return;

      const slide = card.closest('.slick-slide');
      const fitAll = root.dataset.skStoriesFit === 'true';
      const isCenter =
        fitAll ||
        !slide ||
        slide.classList.contains('slick-center') ||
        slide.classList.contains('slick-current');

      if (!isCenter) {
        event.preventDefault();
        event.stopPropagation();
        if ($slick && slide) {
          const idx = Number(slide.getAttribute('data-slick-index'));
          if (!Number.isNaN(idx)) {
            // First click on a side card: center it, then play
            $slick.one('afterChange.skStoriesPlay', () => {
              const centerCard = root.querySelector(
                '.slick-center [data-sk-stories-card], .slick-current [data-sk-stories-card]'
              );
              if (centerCard) togglePlay(centerCard, root);
            });
            $slick.slick('slickGoTo', idx);
          }
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      togglePlay(card, root);
    });

    // Prefetch on hover / focus so first tap is ready
    root.addEventListener(
      'pointerenter',
      (event) => {
        const card = event.target.closest('[data-sk-stories-card]');
        if (card && root.contains(card)) prefetchCard(card);
      },
      true
    );
  }

  function initCarousel(root, force) {
    if (!root) return;
    if (!force && root.dataset.skStoriesReady === 'true') return;
    if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) {
      let tries = 0;
      const wait = setInterval(() => {
        tries += 1;
        if (window.jQuery && window.jQuery.fn && window.jQuery.fn.slick) {
          clearInterval(wait);
          initCarousel(root, force);
        } else if (tries > 40) {
          clearInterval(wait);
        }
      }, 100);
      return;
    }

    const config = parseConfig(root);
    const slickEl = root.querySelector('[data-sk-stories-slick]');
    const prevBtn = root.querySelector('[data-sk-stories-prev]');
    const nextBtn = root.querySelector('[data-sk-stories-next]');
    if (!slickEl) return;

    const $slick = window.jQuery(slickEl);
    if ($slick.hasClass('slick-initialized')) {
      try {
        $slick.slick('unslick');
      } catch (e) {}
    }

    const count = Number(config.count) || slickEl.querySelectorAll('.sk-stories__slide').length;
    const loopRequested = config.loop === true || config.loop === 'true';
    const dots = config.dots !== false;
    const autoplayMs = Number(config.autoplay) > 0 ? Number(config.autoplay) * 1000 : 0;
    const layout = applyLayoutVars(root, config, count);
    const loop = loopRequested && count >= 5 && !layout.fitAll;

    root.dataset.skStoriesReady = 'true';

    $slick.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      variableWidth: true,
      centerMode: !layout.fitAll,
      infinite: loop,
      arrows: false,
      dots: dots && !layout.fitAll,
      dotsClass: 'sk-stories__dots',
      autoplay: !!autoplayMs && !layout.fitAll,
      autoplaySpeed: autoplayMs || 3000,
      speed: 450,
      cssEase: 'cubic-bezier(0.22, 1, 0.36, 1)',
      focusOnSelect: false,
      swipeToSlide: !layout.fitAll,
      adaptiveHeight: false,
      draggable: !layout.fitAll,
      swipe: !layout.fitAll,
    });

    if (!layout.fitAll && count > 0) {
      $slick.slick('slickGoTo', 0, true);
    }

    // Keep track centered when all cards fit
    if (layout.fitAll) {
      requestAnimationFrame(() => {
        try {
          $slick.slick('setPosition');
        } catch (e) {}
        centerFitTrack(root);
      });
    }

    if (prevBtn && !prevBtn.dataset.bound) {
      prevBtn.dataset.bound = 'true';
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (root.dataset.skStoriesFit === 'true') return;
        $slick.slick('slickPrev');
      });
    }
    if (nextBtn && !nextBtn.dataset.bound) {
      nextBtn.dataset.bound = 'true';
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (root.dataset.skStoriesFit === 'true') return;
        $slick.slick('slickNext');
      });
    }

    $slick.off('beforeChange.skStoriesPause').on('beforeChange.skStoriesPause', () => pauseAll(root, null));
    $slick.off('setPosition.skStoriesFit').on('setPosition.skStoriesFit', () => {
      if (root.dataset.skStoriesFit === 'true') centerFitTrack(root);
    });
    $slick.off('afterChange.skStoriesPrefetch').on('afterChange.skStoriesPrefetch', (_e, _slick, current) => {
      const slide = slickEl.querySelector(`.slick-slide[data-slick-index="${current}"]`);
      const card = slide && slide.querySelector('[data-sk-stories-card]');
      if (card) prefetchCard(card);
    });

    root.querySelectorAll('video').forEach((video) => {
      prepareVideo(video);
      video.addEventListener('ended', () => {
        const card = video.closest('[data-sk-stories-card]');
        if (card) card.classList.remove('is-playing');
      });
    });

    // Prefetch first / center card immediately
    const firstCard = root.querySelector(
      '.slick-center [data-sk-stories-card], .slick-current [data-sk-stories-card], [data-sk-stories-card]'
    );
    if (firstCard) prefetchCard(firstCard);

    bindRootClicks(root, $slick);
    root._skStoriesSlick = $slick;
    root._skStoriesConfig = config;
    root._skStoriesCount = count;
  }

  function centerFitTrack(root) {
    const track = root.querySelector('.sk-stories__slick .slick-track');
    const list = root.querySelector('.sk-stories__slick .slick-list');
    if (!track || !list) return;
    const listW = list.clientWidth;
    const trackW = track.scrollWidth;
    const offset = Math.max(0, Math.round((listW - trackW) / 2));
    track.style.transform = `translate3d(${offset}px, 0, 0)`;
  }

  function initAll(scope, force) {
    (scope || document).querySelectorAll(SELECTOR).forEach((root) => {
      if (force) {
        root.dataset.skStoriesReady = '';
        root.dataset.skStoriesClicksBound = '';
      }
      initCarousel(root, force);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target, true);
  });
  document.addEventListener('shopify:section:reorder', () => initAll(document, true));

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document.querySelectorAll(SELECTOR).forEach((root) => {
        const config = root._skStoriesConfig || parseConfig(root);
        const count = root._skStoriesCount || Number(config.count) || 0;
        const prevVisible = root.dataset.skStoriesVisible;
        const prevFit = root.dataset.skStoriesFit;
        const layout = applyLayoutVars(root, config, count);
        const changed =
          prevVisible !== String(layout.visible) || prevFit !== (layout.fitAll ? 'true' : 'false');

        if (changed) {
          root.dataset.skStoriesReady = '';
          root.dataset.skStoriesClicksBound = '';
          initCarousel(root, true);
          return;
        }

        if (root._skStoriesSlick) {
          try {
            root._skStoriesSlick.slick('setPosition');
          } catch (e) {}
        }
        if (layout.fitAll) centerFitTrack(root);
      });
    }, 150);
  });
})();
